const API_URL = process.env.REACT_APP_API_URL || '';

export interface AdminTrendPoint {
  date: string;
  count?: number;
  total?: number;
  ai?: number;
}

export interface AdminMetricsSnapshot {
  users: {
    total: number;
    signupsToday: number;
    signups7d: number;
    signups30d: number;
    wau: number;
    mau: number;
    loginsToday: number;
  };
  subscriptions: {
    trialing: number;
    active: number;
    pastDue: number;
    canceled: number;
    other: number;
    pendingGuestCheckouts: number;
  };
  betfair: {
    connected: number;
    autopilotOn: number;
    connectedToday: number;
  };
  bets: {
    today: number;
    todayAi: number;
    last7d: number;
    last7dAi: number;
  };
  volume: {
    stakedToday: number;
    staked7d: number;
    netPlToday: number;
    netPl7d: number;
  };
  trends: {
    signupsByDay: AdminTrendPoint[];
    betsByDay: AdminTrendPoint[];
  };
  marketing: {
    trackRecordRequests7d: number;
    trackRecordEmails7d: number;
  };
  generatedAt: string;
}

export interface AdminUserRow {
  user_id: string;
  email: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  subscription_status: string | null;
  subscription_current_period_end: string | null;
  betfair_connected: boolean;
  ai_mode: boolean;
  bets_7d: number;
  staked_7d: number;
  net_pl_7d: number;
  last_bet_at: string | null;
}

export interface AdminUsersPageResult {
  users: AdminUserRow[];
  total: number;
  page: number;
  limit: number;
}

export type AdminUserSortColumn =
  | 'email'
  | 'created_at'
  | 'last_sign_in_at'
  | 'subscription_status'
  | 'subscription_current_period_end'
  | 'betfair_connected'
  | 'ai_mode'
  | 'bets_7d'
  | 'staked_7d'
  | 'net_pl_7d'
  | 'last_bet_at';

export type AdminUserSortDir = 'asc' | 'desc';

export interface AdminUsersQuery {
  page: number;
  limit: number;
  search?: string;
  sort?: AdminUserSortColumn;
  order?: AdminUserSortDir;
}

function authHeaders(accessToken: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
}

async function parseApiError(response: Response, fallback: string): Promise<never> {
  try {
    const data = await response.json();
    throw new Error(typeof data?.error === 'string' ? data.error : fallback);
  } catch (e) {
    if (e instanceof Error && e.message !== fallback) throw e;
    throw new Error(fallback);
  }
}

export async function checkAdminAccess(accessToken: string): Promise<boolean> {
  if (!API_URL || !accessToken) return false;

  try {
    const response = await fetch(`${API_URL}/api/admin/me`, {
      method: 'GET',
      credentials: 'include',
      headers: authHeaders(accessToken),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function fetchAdminMetrics(accessToken: string): Promise<AdminMetricsSnapshot> {
  if (!API_URL) throw new Error('API URL is not configured');

  const response = await fetch(`${API_URL}/api/admin/metrics`, {
    method: 'GET',
    credentials: 'include',
    headers: authHeaders(accessToken),
  });

  if (!response.ok) {
    await parseApiError(response, 'Failed to load admin metrics');
  }

  return response.json();
}

export async function fetchAdminUsers(
  accessToken: string,
  query: AdminUsersQuery
): Promise<AdminUsersPageResult> {
  if (!API_URL) throw new Error('API URL is not configured');

  const params = new URLSearchParams({
    page: String(query.page),
    limit: String(query.limit),
  });
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.sort) params.set('sort', query.sort);
  if (query.order) params.set('order', query.order);

  const response = await fetch(`${API_URL}/api/admin/users?${params.toString()}`, {
    method: 'GET',
    credentials: 'include',
    headers: authHeaders(accessToken),
  });

  if (!response.ok) {
    await parseApiError(response, 'Failed to load admin users');
  }

  return response.json();
}
